import { useState, useEffect } from "react";
import { useParams, useNavigate, useSearchParams } from "react-router-dom";
import { format as formatDate } from "date-fns";
import {
  Card,
  CardContent,
  CardFooter,
  CardHeader,
  CardTitle,
  CardDescription,
} from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Calendar } from "@/components/ui/calendar";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Separator } from "@/components/ui/separator";
import { Checkbox } from "@/components/ui/checkbox";
import { cn } from "@/lib/utils";
import {
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbList,
  BreadcrumbSeparator,
} from "@/components/ui/breadcrumb";
import { useToast } from "@/components/ui/use-toast";
import { LoadingDialog } from "@/components/ui/loading-dialog";
import {
  CalendarIcon,
  Clock,
  Users,
  Globe,
  ArrowLeft,
  Building,
  MapPin,
  Target,
  Briefcase,
  HelpCircle,
  Sparkles,
  HandHeart,
  Trophy,
  Loader2,
  ChevronRight,
  User,
  Mail,
  Phone,
  Users2,
  MapPin as MapPinIcon,
  Heart,
  AlertTriangle,
} from "lucide-react";
import {
  fetchPublicCampaignById,
  fetchOpportunityById,
  fetchCampaignOpportunityNpos,
} from "@/lib/api/campaign";
import { useOpportunityApi } from "@/hooks/useOpportunityApi";
import { Opportunity } from "@/hooks/useOpportunitiesApi";
import { supabase } from "@/integrations/supabase/client";
import { createEvent, updateEventWithInvites } from "@/lib/queries/events";
import * as z from "zod";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { Controller } from "react-hook-form";

// Custom styles to remove number input spinners
const noSpinnerStyles = `
  /* For Webkit browsers like Chrome, Safari */
  input[type="number"]::-webkit-outer-spin-button,
  input[type="number"]::-webkit-inner-spin-button {
    -webkit-appearance: none;
    margin: 0;
  }

  /* For Firefox */
  input[type="number"] {
    -moz-appearance: textfield;
  }
`;

// Custom scrollbar styles
const customScrollbarStyles = `
  .custom-scrollbar::-webkit-scrollbar {
    width: 8px;
  }
  
  .custom-scrollbar::-webkit-scrollbar-track {
    background: #f1f1f1;
    border-radius: 10px;
  }
  
  .custom-scrollbar::-webkit-scrollbar-thumb {
    background: #c5c5c5;
    border-radius: 10px;
  }
  
  .custom-scrollbar::-webkit-scrollbar-thumb:hover {
    background: #a8a8a8;
  }
`;

// Duration options in minutes
const durationOptions = [
  { label: "30 minutes", value: 30 },
  { label: "60 minutes", value: 60 },
  { label: "90 minutes", value: 90 },
  { label: "120 minutes", value: 120 },
  { label: "Half day", value: 240 },
  { label: "Full day", value: 480 },
];

// Language options
const languageOptions = [
  "English",
  "Spanish",
  "French",
  "German",
  "Chinese",
  "Japanese",
  "Hindi",
  "Arabic",
  "Portuguese",
  "Russian",
];

// Timezone options - comprehensive list organized by region
const timezoneOptions = [
  // Africa
  { label: "Africa/Cairo (EET)", value: "Africa/Cairo" },
  { label: "Africa/Johannesburg (SAST)", value: "Africa/Johannesburg" },
  { label: "Africa/Lagos (WAT)", value: "Africa/Lagos" },
  { label: "Africa/Nairobi (EAT)", value: "Africa/Nairobi" },

  // America
  { label: "America/Anchorage (AKST/AKDT)", value: "America/Anchorage" },
  { label: "America/Bogota (COT)", value: "America/Bogota" },
  { label: "America/Chicago (CST/CDT)", value: "America/Chicago" },
  { label: "America/Denver (MST/MDT)", value: "America/Denver" },
  { label: "America/Los_Angeles (PST/PDT)", value: "America/Los_Angeles" },
  { label: "America/Mexico_City (CST/CDT)", value: "America/Mexico_City" },
  { label: "America/New_York (EST/EDT)", value: "America/New_York" },
  { label: "America/Phoenix (MST)", value: "America/Phoenix" },
  { label: "America/Sao_Paulo (BRT)", value: "America/Sao_Paulo" },
  { label: "America/Toronto (EST/EDT)", value: "America/Toronto" },
  { label: "America/Vancouver (PST/PDT)", value: "America/Vancouver" },

  // Asia
  { label: "Asia/Bangkok (ICT)", value: "Asia/Bangkok" },
  { label: "Asia/Dubai (GST)", value: "Asia/Dubai" },
  { label: "Asia/Hong_Kong (HKT)", value: "Asia/Hong_Kong" },
  { label: "Asia/Jakarta (WIB)", value: "Asia/Jakarta" },
  { label: "Asia/Kolkata (IST)", value: "Asia/Kolkata" },
  { label: "Asia/Kuala_Lumpur (MYT)", value: "Asia/Kuala_Lumpur" },
  { label: "Asia/Manila (PHT)", value: "Asia/Manila" },
  { label: "Asia/Seoul (KST)", value: "Asia/Seoul" },
  { label: "Asia/Shanghai (CST)", value: "Asia/Shanghai" },
  { label: "Asia/Singapore (SGT)", value: "Asia/Singapore" },
  { label: "Asia/Taipei (CST)", value: "Asia/Taipei" },
  { label: "Asia/Tokyo (JST)", value: "Asia/Tokyo" },

  // Australia
  { label: "Australia/Adelaide (ACST/ACDT)", value: "Australia/Adelaide" },
  { label: "Australia/Brisbane (AEST)", value: "Australia/Brisbane" },
  { label: "Australia/Melbourne (AEST/AEDT)", value: "Australia/Melbourne" },
  { label: "Australia/Perth (AWST)", value: "Australia/Perth" },
  { label: "Australia/Sydney (AEST/AEDT)", value: "Australia/Sydney" },

  // Europe
  { label: "Europe/Amsterdam (CET/CEST)", value: "Europe/Amsterdam" },
  { label: "Europe/Athens (EET/EEST)", value: "Europe/Athens" },
  { label: "Europe/Berlin (CET/CEST)", value: "Europe/Berlin" },
  { label: "Europe/Brussels (CET/CEST)", value: "Europe/Brussels" },
  { label: "Europe/Dublin (GMT/IST)", value: "Europe/Dublin" },
  { label: "Europe/Istanbul (TRT)", value: "Europe/Istanbul" },
  { label: "Europe/London (GMT/BST)", value: "Europe/London" },
  { label: "Europe/Madrid (CET/CEST)", value: "Europe/Madrid" },
  { label: "Europe/Moscow (MSK)", value: "Europe/Moscow" },
  { label: "Europe/Paris (CET/CEST)", value: "Europe/Paris" },
  { label: "Europe/Rome (CET/CEST)", value: "Europe/Rome" },
  { label: "Europe/Stockholm (CET/CEST)", value: "Europe/Stockholm" },
  { label: "Europe/Zurich (CET/CEST)", value: "Europe/Zurich" },

  // Pacific
  { label: "Pacific/Auckland (NZST/NZDT)", value: "Pacific/Auckland" },
  { label: "Pacific/Fiji (FJT/FJST)", value: "Pacific/Fiji" },
  { label: "Pacific/Honolulu (HST)", value: "Pacific/Honolulu" },
];

// Helper function to format focus areas for display
const formatFocusArea = (focusArea: string): string => {
  return focusArea
    .split("_")
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(" ");
};

// Get date range for booking (starting 2 weeks from now)
const today = new Date();
const twoWeeksFromNow = new Date();
twoWeeksFromNow.setDate(today.getDate() + 4); // Booking available starting 2 weeks from now

// Mock NPO data (in a real app, this would come from an API)
interface NPO {
  id: string;
  name: string;
  logo?: string;
  description?: string;
  location: string;
  causes?: string[];
  website?: string;
  catalogId?: string;
  supportedLocations?: string[]; // Array of supported locations
}

// Booking phase type
type BookingPhase = 1 | 2 | 3;

// Form validation schema
const bookingFormSchema = z.object({
  // Event details
  event_date: z
    .string()
    .min(1, "Event date is required")
    .refine((date) => {
      const selectedDate = new Date(date);
      const today = new Date();
      // Set both dates to start of day for accurate comparison
      selectedDate.setHours(0, 0, 0, 0);
      today.setHours(0, 0, 0, 0);

      // Calculate difference in days
      const diffTime = selectedDate.getTime() - today.getTime();
      const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));

      return diffDays >= 14; // Minimum 2 weeks (14 days)
    }, "Event date must be at least 2 weeks in the future"),
  event_time: z.string().min(1, "Event time is required"),
  duration: z.number().min(1, "Duration is required"),
  volunteer_count: z.number().min(1, "Number of volunteers is required"),
  preferred_language: z.string().optional(),
  timezone: z.string().optional(),
  location: z.string().optional(),
  description: z.string().optional(),

  // NPO selection
  npo_id: z.string().min(1, "NPO selection is required"),
  catalog_id: z.string().optional(),
  campaign_id: z.string().optional(),
  expected_participants: z.number().optional(),

  // Booker details
  name: z.string().min(1, "Full name is required"),
  email: z.string().email("Invalid email address"),
  phone: z.string().optional(),
  team_name: z.string().optional(),
  address: z.string().min(1, "Address is required"),
});

type BookingFormData = z.infer<typeof bookingFormSchema>;

const EventBookingPage = () => {
  const { opportunityId, campaignId, corporateName } = useParams<{
    opportunityId: string;
    campaignId: string;
    corporateName: string;
  }>();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { toast } = useToast();

  // Campaign and opportunity data
  const [campaign, setCampaign] = useState<any>(null);
  const [opportunity, setOpportunity] = useState<any>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // NPO selection
  const [npos, setNpos] = useState<NPO[]>([]);
  const [allNpos, setAllNpos] = useState<NPO[]>([]);
  const [isLoadingNpos, setIsLoadingNpos] = useState(false);
  const [nposFiltered, setNposFiltered] = useState(false);

  // Booking phases
  const [bookingPhase, setBookingPhase] = useState<number>(1);

  // Submission state
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
  const [showLoadingDialog, setShowLoadingDialog] = useState(false);
  const [isMicrosite, setIsMicrosite] = useState(false);

  // React Hook Form
  const form = useForm<BookingFormData>({
    resolver: zodResolver(bookingFormSchema),
    defaultValues: {
      event_date: "",
      event_time: "",
      duration: 60,
      volunteer_count: 1,
      preferred_language: "English",
      timezone: "Asia/Kolkata",
      npo_id: "",
      catalog_id: "",
      campaign_id: campaignId || "",
      description: "",
      location: "",
      expected_participants: 0,
      name: "",
      email: "",
      phone: "",
      team_name: "",
      address: "",
      // agree_to_terms removed as we're using a label instead
    },
  });

  // Extract form methods for easier access
  const {
    register,
    setValue,
    getValues,
    watch,
    control,
    formState: { errors },
  } = form;

  // Check for microsite parameter
  useEffect(() => {
    const source = searchParams.get("source");
    const ref = searchParams.get("ref");

    if (source === "microsite" && ref) {
      sessionStorage.setItem("microsite_exp", "true");
      setIsMicrosite(true);
    }
  }, [searchParams]);

  // Check for payment status
  useEffect(() => {
    const sessionId = searchParams.get("session_id");
    const paymentStatus = searchParams.get("payment");

    if (sessionId) {
      setIsMicrosite(sessionStorage.getItem("microsite_exp") === "true");
      if (paymentStatus === "success") {
        toast({
          title: "Payment successful",
          description: "Your booking has been confirmed.",
        });

        if (isMicrosite) {
          // Post message to parent window and close
          window.opener?.postMessage(
            {
              type: "CHECKOUT_RESULT",
              success: true,
              message: "Payment successful",
            },
            "*"
          );
          window.close();
        } else {
          navigate("/checkout/thank-you");
        }
      } else if (paymentStatus === "failed" || paymentStatus === "cancelled") {
        toast({
          title: "Payment failed",
          description: "Please try again or contact support.",
          variant: "destructive",
        });

        if (isMicrosite) {
          // Post message to parent window and close
          window.opener?.postMessage(
            {
              type: "CHECKOUT_RESULT",
              success: false,
              message: "Payment failed",
            },
            "*"
          );
          window.close();
        }
        // Stay on the same page with the error toast
      }
    }
  }, [navigate, toast, isMicrosite, searchParams]);

  // Load saved form data from session storage
  useEffect(() => {
    const savedFormData = sessionStorage.getItem("savedFormData");

    if (savedFormData) {
      const parsedData = JSON.parse(savedFormData);
      Object.keys(parsedData).forEach((key) => {
        setValue(key as any, parsedData[key]);
      });
    }
  }, [setValue]);

  // Fetch campaign and opportunity data
  useEffect(() => {
    const fetchData = async () => {
      try {
        setIsLoading(true);

        if (!campaignId || !opportunityId) {
          throw new Error("Campaign ID or Opportunity ID is missing");
        }

        // Fetch campaign data
        const campaignData = await fetchPublicCampaignById(campaignId);
        setCampaign(campaignData.data);

        // Fetch opportunity data
        const opportunityData = await fetchOpportunityById("", opportunityId);
        setOpportunity(opportunityData);

        // Set campaign_id in the form
        setValue("campaign_id", campaignId);

        // Set description from opportunity if available
        if (opportunityData?.data?.description) {
          setValue("description", opportunityData.data.description);
        }

        // Set location from opportunity if available
        if (opportunityData?.data?.location) {
          setValue("location", opportunityData.data.location);
        }

        setIsLoading(false);
      } catch (err) {
        setIsLoading(false);
        setError(
          err instanceof Error
            ? err.message
            : "An error occurred while fetching data"
        );
        console.error("Error fetching data:", err);
      }
    };

    fetchData();
  }, [campaignId, opportunityId, setValue]);

  // Fetch NPOs when moving to phase 2
  useEffect(() => {
    const fetchNpos = async () => {
      // Only fetch NPOs if we're in phase 2 and we have a campaign and opportunity
      if (bookingPhase === 2 && campaignId && opportunityId) {
        try {
          setIsLoadingNpos(true);

          // Fetch NPOs that can support this opportunity in this campaign
          const response = await fetchCampaignOpportunityNpos(
            "",
            campaignId,
            opportunityId
          );
          // Transform the API response to match our NPO interface
          const formattedNpos = response.data.map((item: any) => ({
            id: item.npo.id,
            name: item.npo.org_name,
            logo: item.npo.logo_url,
            description: "", // Not provided in the response
            location: `${item.npo.city}, ${item.npo.state}, ${item.npo.country}`
              .replace(/, ,/g, ",")
              .replace(/^, /, "")
              .replace(/, $/, ""),
            causes: [], // Not provided in the response
            website: item.npo.website,
            catalogId: item.catalog,
            // Store the original location data for filtering
            city: item.npo.city,
            state: item.npo.state,
            country: item.npo.country,
          }));

          // Store all NPOs
          setAllNpos(formattedNpos);

          // IMPORTANT: We need to set the supportedLocations for each NPO
          // This is needed to display the supported locations in the NPO card
          if (campaign?.map && Array.isArray(campaign.map)) {
            // Get the selected location from step 1
            const selectedLocation = watch("location");
            if (selectedLocation) {
              // Parse the selected location (format: "district, state, country")
              const locationParts = selectedLocation.split(", ");
              if (locationParts.length >= 3) {
                const [district, state, country] = locationParts;

                // Create location key in the format "country-state-district"
                const locationKey = `${country}-${state}-${district}`;

                // Group map items by NPO ID
                const npoLocationMap = new Map();
                const npoOpportunityMap = new Map();

                // Collect locations and opportunities for each NPO ID
                campaign.map.forEach((mapItem: any) => {
                  if (mapItem.npoId) {
                    // Process locations
                    if (
                      mapItem.supported_locations &&
                      Array.isArray(mapItem.supported_locations)
                    ) {
                      if (!npoLocationMap.has(mapItem.npoId)) {
                        npoLocationMap.set(mapItem.npoId, new Set());
                      }
                      mapItem.supported_locations.forEach((loc: string) => {
                        npoLocationMap.get(mapItem.npoId).add(loc.trim());
                      });
                    }

                    // Process opportunities
                    if (
                      mapItem.supported_categories &&
                      Array.isArray(mapItem.supported_categories)
                    ) {
                      if (!npoOpportunityMap.has(mapItem.npoId)) {
                        npoOpportunityMap.set(mapItem.npoId, new Set());
                      }
                      mapItem.supported_categories.forEach((catId: string) => {
                        npoOpportunityMap.get(mapItem.npoId).add(catId.trim());
                      });
                    }
                  }
                });

                // Set the supportedLocations for each NPO
                formattedNpos.forEach((npo) => {
                  if (npoLocationMap.has(npo.id)) {
                    // Convert Set to Array for easier rendering
                    npo.supportedLocations = Array.from(
                      npoLocationMap.get(npo.id)
                    ).map((loc: string) => {
                      // Convert location key format (country-state-district) to readable format
                      const parts = loc.split("-");
                      if (parts.length === 3) {
                        return `${parts[2]}, ${parts[1]}, ${parts[0]}`;
                      }
                      return loc;
                    });
                  }
                });

                // Find NPOs that support the selected location and opportunity
                if (!nposFiltered) {
                  const npoIdsWithLocation = new Set();
                  const npoIdsWithOpportunity = new Set();

                  // Find NPOs that support the selected location
                  npoLocationMap.forEach((locations, npoId) => {
                    if (locations.has(locationKey)) {
                      npoIdsWithLocation.add(npoId);
                    }
                  });

                  // Find NPOs that support the selected opportunity
                  npoOpportunityMap.forEach((opportunities, npoId) => {
                    if (opportunities.has(opportunityId)) {
                      npoIdsWithOpportunity.add(npoId);
                    }
                  });

                  // Only include NPOs that support BOTH location AND opportunity
                  const supportedNpoIds = new Set();
                  npoIdsWithLocation.forEach((npoId) => {
                    if (npoIdsWithOpportunity.has(npoId)) {
                      supportedNpoIds.add(npoId);
                    }
                  });

                  // Filter NPOs based on supported IDs
                  const filteredNpos = formattedNpos.filter((npo) =>
                    supportedNpoIds.has(npo.id)
                  );
                  console.log("Filtered NPOs count:", filteredNpos.length);

                  // Update the NPO list with filtered results
                  if (filteredNpos.length > 0) {
                    setNpos(filteredNpos);
                  } else {
                    setNpos([]);
                  }

                  // Mark as filtered to prevent duplicate filtering
                  setNposFiltered(true);
                }
              }
            }
          }

          setIsLoadingNpos(false);
        } catch (err) {
          setIsLoadingNpos(false);
          console.error("Error fetching NPOs:", err);
          toast({
            title: "Error",
            description: "Failed to load NPO partners. Please try again.",
            variant: "destructive",
          });
        }
      }
    };

    fetchNpos();
  }, [
    bookingPhase,
    campaignId,
    opportunityId,
    toast,
    nposFiltered,
    watch,
    campaign,
  ]);

  // Extract opportunity data from the response
  const opportunityData = opportunity?.data;
  const errorData = error ? (error as any).message : null;

  // Validate event details
  const validateEventDetails = (): boolean => {
    if (!watch("event_date") || !watch("event_time")) {
      toast({
        title: "Missing Information",
        description: "Please select both date and time for your event.",
        variant: "destructive",
      });
      return false;
    }
    return true;
  };

  // Handle proceeding to NPO selection
  const handleProceedToNPOSelection = () => {
    if (validateEventDetails()) {
      // Update the NPO list based on the selected location before changing the phase
      if (allNpos.length > 0) {
        const selectedLocation = watch("location");
        if (selectedLocation && campaign?.map) {
          // Parse the selected location (format: "district, state, country")
          const locationParts = selectedLocation.split(", ");
          if (locationParts.length >= 3) {
            const [district, state, country] = locationParts;

            // Create location key in the format "country-state-district" (as in the example)
            const locationKey = `${country}-${state}-${district}`;

            // Get the list of NPO IDs that support BOTH this location AND this opportunity from the campaign map
            const supportedNpoIds = new Set();

            // Process map items more efficiently with less logging
            if (campaign.map && Array.isArray(campaign.map)) {
              // Group map items by NPO ID to avoid duplicate processing
              const npoLocationMap = new Map();
              const npoOpportunityMap = new Map();

              // First pass: collect all locations and supported opportunities for each NPO ID
              campaign.map.forEach((mapItem: any) => {
                if (mapItem.npoId) {
                  // Process supported locations
                  if (
                    mapItem.supported_locations &&
                    Array.isArray(mapItem.supported_locations)
                  ) {
                    if (!npoLocationMap.has(mapItem.npoId)) {
                      npoLocationMap.set(mapItem.npoId, new Set());
                    }

                    // Add all locations for this NPO ID
                    mapItem.supported_locations.forEach((loc: string) => {
                      npoLocationMap.get(mapItem.npoId).add(loc.trim());
                    });
                  }

                  // Process supported opportunities/categories
                  if (
                    mapItem.supported_categories &&
                    Array.isArray(mapItem.supported_categories)
                  ) {
                    if (!npoOpportunityMap.has(mapItem.npoId)) {
                      npoOpportunityMap.set(mapItem.npoId, new Set());
                    }

                    // Add all supported opportunities for this NPO ID
                    mapItem.supported_categories.forEach((catId: string) => {
                      npoOpportunityMap.get(mapItem.npoId).add(catId.trim());
                    });
                  }
                }
              });

              // Find NPOs that support BOTH the selected location AND the selected opportunity
              const npoIdsWithLocation = new Set();
              const npoIdsWithOpportunity = new Set();

              // Find NPOs that support the selected location
              npoLocationMap.forEach((locations, npoId) => {
                if (locations.has(locationKey)) {
                  npoIdsWithLocation.add(npoId);
                }
              });

              // Find NPOs that support the selected opportunity
              npoOpportunityMap.forEach((opportunities, npoId) => {
                if (opportunities.has(opportunityId)) {
                  npoIdsWithOpportunity.add(npoId);
                }
              });

              // Only include NPOs that support BOTH the location AND opportunity
              npoIdsWithLocation.forEach((npoId) => {
                if (npoIdsWithOpportunity.has(npoId)) {
                  supportedNpoIds.add(npoId);
                }
              });

              // Store the supported locations for each NPO (inside the same scope as npoLocationMap)
              allNpos.forEach((npo) => {
                if (npoLocationMap.has(npo.id)) {
                  // Convert Set to Array for easier rendering
                  npo.supportedLocations = Array.from(
                    npoLocationMap.get(npo.id)
                  ).map((loc: string) => {
                    // Convert location key format (country-state-district) to readable format
                    const parts = loc.split("-");
                    if (parts.length === 3) {
                      return `${parts[2]}, ${parts[1]}, ${parts[0]}`;
                    }
                    return loc;
                  });
                }
              });
            }

            // Filter NPOs based on the supported NPO IDs from the map
            const filteredNpos = allNpos.filter((npo) =>
              supportedNpoIds.has(npo.id)
            );

            // If we have filtered NPOs, use them; otherwise fall back to all NPOs
            if (filteredNpos.length > 0) {
              setNpos(filteredNpos);
              setNposFiltered(true);
            } else {
              setNpos([]);
              setNposFiltered(true);
            }
          } else {
            setNpos(allNpos);
            setNposFiltered(true);
          }
        } else {
          setNpos(allNpos);
          setNposFiltered(true);
        }
      }

      // Always set the booking phase after filtering NPOs
      // This ensures the filtered NPOs are displayed correctly
      setBookingPhase(2);
      // Scroll to top of page
      window.scrollTo({ top: 0, behavior: "smooth" });
    }
  };

  // Validate NPO selection
  const validateNPOSelection = (): boolean => {
    if (!watch("npo_id")) {
      toast({
        title: "Missing Information",
        description: "Please select an NPO.",
        variant: "destructive",
      });
      return false;
    }
    return true;
  };

  // Handle proceeding to booker details
  const handleProceedToBookerDetails = () => {
    if (validateNPOSelection()) {
      setBookingPhase(3);
      // Scroll to top of page
      window.scrollTo({ top: 0, behavior: "smooth" });
    }
  };

  // Validate booker details
  const validateBookerDetails = (): boolean => {
    if (!watch("name") || !watch("email") || !watch("address")) {
      toast({
        title: "Missing Information",
        description:
          "Please fill in all required fields and agree to the terms and conditions.",
        variant: "destructive",
      });
      return false;
    }

    // Basic email validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(watch("email"))) {
      toast({
        title: "Invalid Email",
        description: "Please enter a valid email address.",
        variant: "destructive",
      });
      return false;
    }

    return true;
  };

  // Function to calculate event cost
  const calculateEventCost = (): number => {
    if (!opportunity?.data?.price_recommendations) return 0;

    const pricingModel = opportunity.data.price_recommendations.pricing_model;
    const baseCost =
      parseFloat(opportunity.data.price_recommendations.cost_per_unit) || 0;
    const volunteerCount = parseInt(
      String(watch("volunteer_count") || "0"),
      10
    );

    // Calculate base cost based on pricing model
    let totalCost =
      pricingModel === "per_volunteer" ? baseCost * volunteerCount : baseCost;

    // Add additional fees if they exist
    const additionalFees =
      opportunity.data.price_recommendations.additional_fees || {};
    Object.values(additionalFees).forEach((fee: any) => {
      if (fee.amount) {
        if (fee.fee_type === "per_volunteer") {
          totalCost += parseFloat(fee.amount) * volunteerCount;
        } else if (fee.fee_type === "per_event") {
          totalCost += parseFloat(fee.amount);
        } else {
          totalCost += parseFloat(fee.amount); // total fee
        }
      }
    });

    return totalCost;
  };

  // Handle form submission
  const handleSubmit = async () => {
    if (!validateBookerDetails()) {
      return;
    }

    setIsSubmitting(true);
    setShowLoadingDialog(true);

    try {
      // Save form data to session storage
      const formDataToSave = {
        ...getValues(),
      };
      sessionStorage.setItem("savedFormData", JSON.stringify(formDataToSave));

      // Call sign-in-otp edge function
      const response = await fetch(
        `${import.meta.env.VITE_CC_API_URL}/v1/hooks/otp`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            email: watch("email"),
            userData: {
              first_name: watch("name").split(" ")[0],
              last_name: watch("name").split(" ").slice(1).join(" "),
              phone: watch("phone"),
            },
          }),
        }
      );

      if (!response.ok) {
        throw new Error("Failed to send OTP");
      }

      const { data: signInData } = await response.json();
      const { userId } = signInData;

      if (!userId) {
        throw new Error("Failed to create temporary user");
      }

      sessionStorage.setItem("tempUserId", userId);

      // Update profiles using RPC
      const { error: txError } = await supabase.rpc(
        "update_corporate_profile",
        {
          user_id: userId,
          profile_full_name: watch("name"),
          corp_first_name: watch("name").split(" ")[0],
          corp_last_name: watch("name").split(" ").slice(1).join(" "),
          corp_isd_code: "+65", // Default ISD code
          corp_phone: watch("phone"),
          corp_company_email: watch("email"),
        }
      );

      if (txError) {
        console.error("Error updating profiles:", txError);
        throw txError;
      }

      // Format the date in ISO format with timezone
      const eventDate = watch("event_date");
      const eventTime = watch("event_time");
      const timezone = watch("timezone") || "UTC";

      // Create ISO format date string with timezone
      let formattedDate = eventDate;
      let isoDateTimeString = "";

      try {
        // Validate inputs before proceeding
        if (!eventDate || !eventTime || !timezone) {
          throw new Error("Missing required date, time, or timezone");
        }

        // Handle the case where eventDate might be a Date object or a string with timezone info
        let dateToUse;
        if (eventDate instanceof Date) {
          dateToUse = eventDate;
        } else if (typeof eventDate === "string") {
          // Remove any timezone information if present
          const cleanDateStr = eventDate.split("T")[0];
          dateToUse = new Date(cleanDateStr);
        } else {
          throw new Error(`Unexpected event date format: ${eventDate}`);
        }

        // Parse time
        const timeStr = String(eventTime);
        const timeParts = timeStr.split(":");
        if (timeParts.length < 2) {
          throw new Error(`Invalid time format: ${timeStr}`);
        }

        const hours = parseInt(timeParts[0], 10);
        const minutes = parseInt(timeParts[1], 10);

        // Validate time parts
        if (isNaN(hours) || isNaN(minutes)) {
          throw new Error("Invalid time format");
        }

        // Set the time on our date object
        dateToUse.setHours(hours);
        dateToUse.setMinutes(minutes);
        dateToUse.setSeconds(0);
        dateToUse.setMilliseconds(0);

        // Check if date is valid
        if (isNaN(dateToUse.getTime())) {
          throw new Error("Invalid date created");
        }

        // Format date for ISO string (YYYY-MM-DD)
        const year = dateToUse.getFullYear();
        const month = dateToUse.getMonth() + 1; // Month is 0-indexed
        const day = dateToUse.getDate();
        formattedDate = `${year}-${month.toString().padStart(2, "0")}-${day
          .toString()
          .padStart(2, "0")}`;

        // Format time (HH:MM:SS)
        const formattedHours = dateToUse.getHours().toString().padStart(2, "0");
        const formattedMinutes = dateToUse
          .getMinutes()
          .toString()
          .padStart(2, "0");

        // Create ISO string with timezone included directly in the date string
        // All timezone options in the dropdown are IANA timezone identifiers
        // Format: YYYY-MM-DDThh:mm:ss[Timezone]

        // Get the selected timezone from the form
        const selectedTimezone = watch("timezone") || "UTC";

        // Format the timezone string according to ISO 8601 with IANA timezone extension
        // This uses the bracket notation for IANA timezone identifiers
        const timezoneString =
          selectedTimezone === "UTC" ? "Z" : `[${selectedTimezone}]`;

        // Append timezone to the ISO date string
        isoDateTimeString = `${formattedDate}T${formattedHours}:${formattedMinutes}:00${timezoneString}`;

        console.log(
          "Successfully formatted date with timezone:",
          isoDateTimeString
        );
      } catch (error) {
        console.error("Error formatting date:", error);
        // Fall back to original date if there's an error
        formattedDate = eventDate;
        if (typeof eventDate === "string" && typeof eventTime === "string") {
          isoDateTimeString = `${eventDate.split("T")[0]}T${eventTime}`;
        } else {
          // Last resort fallback
          const now = new Date();
          isoDateTimeString = now.toISOString().split("Z")[0];
        }
      }

      // Calculate event cost (in a real implementation, this would come from the API)
      const eventCost = opportunity?.data?.event_cost || 0;

      // Create the event with pending_payment status
      const existingEventId = sessionStorage.getItem("checkOutItemId");

      let createdEvent;
      if (existingEventId) {
        // Update existing event
        const { error: updateError } = await updateEventWithInvites({
          id: existingEventId,
          eventData: {
            opportunity_catalog_id: watch("catalog_id"),
            npo_id: watch("npo_id"),
            event_name: opportunity?.data?.name,
            description: watch("description") || opportunity?.data?.description,
            corporate_id: userId,
            status: isMicrosite ? "payment_done" : "payment_pending",
            event_date: isoDateTimeString,
            event_time: watch("event_time"),
            paylater: eventCost == 0 || Boolean(isMicrosite),
            campaign_id: watch("campaign_id"),
            preferred_language: watch("preferred_language"),
            location: watch("address"),
            expected_participants: watch("volunteer_count"),
            meta: {
              team_name: watch("team_name") || "",
              timezone: watch("timezone") || "UTC",
              currency:
                opportunity?.data?.price_recommendations?.currency || "INR",
              event_cost: calculateEventCost(),
              npo_location: watch("location") || "",
              remarks: watch("description") || "",
            },
          },
        });

        if (updateError) {
          throw updateError;
        }
        createdEvent = existingEventId;
      } else {
        // Create new event
        console.log("Creating new event");
        const { id: newEventId } = await createEvent({
          opportunity_catalog_id: watch("catalog_id"),
          npo_id: watch("npo_id"),
          event_name: opportunity?.data?.name,
          description: watch("description") || opportunity?.data?.description,
          corporate_id: userId,
          status: isMicrosite ? "payment_done" : "payment_pending",
          event_date: isoDateTimeString,
          event_time: watch("event_time"),
          paylater: eventCost == 0 || Boolean(isMicrosite),
          campaign_id: watch("campaign_id"),
          preferred_language: watch("preferred_language"),
          location: watch("address"),
          expected_participants: watch("volunteer_count"),
          meta: {
            team_name: watch("team_name") || "",
            timezone: watch("timezone") || "UTC",
            currency:
              opportunity?.data?.price_recommendations?.currency || "INR",
            event_cost: calculateEventCost(),
            npo_location: watch("location") || "",
            remarks: watch("description") || "",
          },
        });

        if (!newEventId) {
          throw new Error("Failed to create event");
        }
        createdEvent = newEventId;
        sessionStorage.setItem("checkOutItemId", createdEvent);
        sessionStorage.setItem("retryUrl", window.location.href);
      }

      if (!createdEvent) {
        throw new Error("Failed to create/update event");
      }

      // If microsite or zero cost, skip payment
      if (isMicrosite || eventCost === 0) {
        // Show success message
        toast({
          title: "Booking Successful",
          description: "Your event has been booked successfully!",
        });

        // Navigate to thank you page
        navigate("/checkout/thank-you");
        window.location.reload();
      } else {
        // Create Stripe checkout session
        const response = await fetch(
          `${import.meta.env.VITE_CC_API_URL}/v1/hooks/stripe/checkout-session`,
          {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
            },
            body: JSON.stringify({
              eventCost,
              eventName: opportunity?.data?.name || "Event Booking",
              eventId: createdEvent,
              email: watch("email"),
              phoneNumber: watch("phone"),
              successUrl: `${window.location.origin}/corporate/${corporateName}/events/${campaignId}/book/${opportunityId}?payment=success&session_id={CHECKOUT_SESSION_ID}`,
              cancelUrl: `${window.location.origin}/corporate/${corporateName}/events/${campaignId}/book/${opportunityId}?payment=cancelled&session_id={CHECKOUT_SESSION_ID}`,
            }),
          }
        );

        if (!response.ok) {
          throw new Error("Failed to create checkout session");
        }

        const { data: sessionData } = await response.json();

        // Store event ID for retry scenarios
        sessionStorage.setItem("checkOutItemId", createdEvent);
        sessionStorage.setItem("retryUrl", window.location.href);

        // Redirect to Stripe checkout
        window.location.href = sessionData.sessionUrl;
      }
    } catch (err) {
      console.error("Error booking event:", err);

      toast({
        title: "Booking Failed",
        description:
          err instanceof Error
            ? err.message
            : "There was an error booking your event. Please try again.",
        variant: "destructive",
      });
      setShowLoadingDialog(false);
    } finally {
      setIsSubmitting(false);
    }
  };

  if (isLoading) {
    return (
      <div className="flex flex-col justify-center items-center min-h-screen bg-gradient-to-b from-blue-50 to-white">
        <div className="text-center p-8">
          <Loader2 className="h-16 w-16 animate-spin text-blue-600 mx-auto mb-6" />
          <h2 className="text-2xl font-bold text-blue-800 mb-2">
            Loading Opportunity
          </h2>
          <p className="text-gray-600">
            Please wait while we prepare your booking page...
          </p>
        </div>
      </div>
    );
  }

  if (error || !opportunity) {
    return (
      <div className="flex flex-col justify-center items-center min-h-screen bg-gradient-to-b from-blue-50 to-white p-4">
        <div className="bg-white rounded-lg shadow-xl p-8 max-w-md text-center">
          <h1 className="text-2xl font-bold text-red-600 mb-4">
            Oops! Something went wrong
          </h1>
          <p className="text-gray-700 mb-6">
            {error || "Opportunity data not available"}
          </p>
          <Button
            className="bg-blue-600 hover:bg-blue-700 transition-colors"
            onClick={() => navigate(-1)}
          >
            <ArrowLeft className="mr-2 h-4 w-4" />
            Go Back
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-blue-50 via-white to-blue-50">
      {/* Decorative Elements */}
      <div className="absolute top-0 left-0 w-full h-full overflow-hidden pointer-events-none z-0">
        <div className="absolute top-10 left-10 w-64 h-64 rounded-full bg-blue-200 opacity-20 blur-3xl"></div>
        <div className="absolute top-40 right-20 w-80 h-80 rounded-full bg-purple-200 opacity-20 blur-3xl"></div>
        <div className="absolute bottom-20 left-1/4 w-96 h-96 rounded-full bg-green-200 opacity-20 blur-3xl"></div>
      </div>

      <style>{noSpinnerStyles}</style>
      <style>{customScrollbarStyles}</style>

      <div className="container mx-auto max-w-7xl px-4 py-8 relative z-10">
        {/* Breadcrumb */}
        <Breadcrumb className="mb-6">
          <BreadcrumbList>
            <BreadcrumbItem>
              <BreadcrumbLink
                href={`/corporate/${corporateName}/events/${campaignId}`}
              >
                <ArrowLeft className="mr-2 h-4 w-4 inline" />
                Back to Campaign
              </BreadcrumbLink>
            </BreadcrumbItem>
            <BreadcrumbSeparator />
            <BreadcrumbItem>
              <BreadcrumbLink>Book Event</BreadcrumbLink>
            </BreadcrumbItem>
          </BreadcrumbList>
        </Breadcrumb>

        {/* Booking Progress */}
        <div className="mb-8">
          <div className="flex items-center justify-center">
            <div
              className={`flex flex-col items-center ${
                bookingPhase === 1 ? "text-blue-600" : "text-gray-500"
              }`}
            >
              <div
                className={`w-10 h-10 rounded-full flex items-center justify-center mb-2 ${
                  bookingPhase === 1
                    ? "bg-blue-600 text-white"
                    : "bg-gray-200 text-gray-500"
                }`}
              >
                1
              </div>
              <span className="text-sm font-medium">Event Details</span>
            </div>
            <div
              className={`w-20 h-1 mx-2 ${
                bookingPhase === 2 ? "bg-blue-600" : "bg-gray-200"
              }`}
            ></div>
            <div
              className={`flex flex-col items-center ${
                bookingPhase === 2 ? "text-blue-600" : "text-gray-500"
              }`}
            >
              <div
                className={`w-10 h-10 rounded-full flex items-center justify-center mb-2 ${
                  bookingPhase === 2
                    ? "bg-blue-600 text-white"
                    : "bg-gray-200 text-gray-500"
                }`}
              >
                2
              </div>
              <span className="text-sm font-medium">Select NPO</span>
            </div>
            <div
              className={`w-20 h-1 mx-2 ${
                bookingPhase === 3 ? "bg-blue-600" : "bg-gray-200"
              }`}
            ></div>
            <div
              className={`flex flex-col items-center ${
                bookingPhase === 3 ? "text-blue-600" : "text-gray-500"
              }`}
            >
              <div
                className={`w-10 h-10 rounded-full flex items-center justify-center mb-2 ${
                  bookingPhase === 3
                    ? "bg-blue-600 text-white"
                    : "bg-gray-200 text-gray-500"
                }`}
              >
                3
              </div>
              <span className="text-sm font-medium">Booker Details</span>
            </div>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Opportunity Details */}
          <div className="lg:col-span-1">
            <Card className="shadow-lg border-none bg-white/90 backdrop-blur-sm sticky top-8">
              <CardHeader>
                <CardTitle className="text-2xl font-bold text-blue-800">
                  Opportunity Details
                </CardTitle>
                <CardDescription>
                  Review the details before booking
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-6">
                {/* Opportunity Image */}
                <div className="relative h-48 overflow-hidden rounded-md">
                  {opportunityData?.image_url ? (
                    <img
                      src={opportunityData.image_url}
                      alt={opportunityData.name}
                      className="w-full h-full object-cover"
                    />
                  ) : (
                    <div className="flex items-center justify-center w-full h-full bg-gradient-to-r from-blue-100 to-indigo-100 text-blue-500">
                      <Briefcase className="w-16 h-16" />
                    </div>
                  )}
                </div>

                {/* Opportunity Name */}
                <h3 className="text-xl font-bold text-blue-800">
                  {opportunityData?.name}
                </h3>

                {/* Format Badges */}
                <div className="flex flex-wrap gap-2">
                  <div className="flex items-center bg-blue-100 text-blue-800 px-3 py-1 rounded-full text-sm">
                    <Building className="w-4 h-4 mr-1" />
                    <span>
                      {opportunityData?.format
                        ? formatFocusArea(opportunityData.format)
                        : ""}
                    </span>
                  </div>
                  <div className="flex items-center bg-red-100 text-red-800 px-3 py-1 rounded-full text-sm">
                    <MapPin className="w-4 h-4 mr-1" />
                    <span>{opportunityData?.location}</span>
                  </div>
                  <div className="flex items-center bg-green-100 text-green-800 px-3 py-1 rounded-full text-sm">
                    <Target className="w-4 h-4 mr-1" />
                    <span>
                      {opportunityData?.focus_area
                        ? formatFocusArea(opportunityData.focus_area)
                        : ""}
                    </span>
                  </div>
                </div>

                {/* Community Need */}
                <div>
                  <h4 className="font-semibold text-gray-700 flex items-center mb-2">
                    <HelpCircle className="w-4 h-4 mr-2 text-blue-600" />
                    Community Need
                  </h4>
                  <p className="text-gray-600 text-sm">
                    {opportunityData?.community_need}
                  </p>
                </div>

                {/* Impact */}
                <div>
                  <h4 className="font-semibold text-gray-700 flex items-center mb-2">
                    <Sparkles className="w-4 h-4 mr-2 text-purple-600" />
                    Impact
                  </h4>
                  <p className="text-gray-600 text-sm">
                    {opportunityData?.impact}
                  </p>
                </div>

                {/* Support Type */}
                <div>
                  <h4 className="font-semibold text-gray-700 flex items-center mb-2">
                    <HandHeart className="w-4 h-4 mr-2 text-red-600" />
                    Support Type
                  </h4>
                  <p className="text-gray-600 text-sm">
                    {opportunityData?.meta?.volunteers_do}
                  </p>
                </div>

                {/* Outcome */}
                <div>
                  <h4 className="font-semibold text-gray-700 flex items-center mb-2">
                    <Trophy className="w-4 h-4 mr-2 text-amber-600" />
                    Outcome
                  </h4>
                  <p className="text-gray-600 text-sm">
                    {opportunityData?.outcome}
                  </p>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Booking Form */}
          <div className="lg:col-span-2">
            <Card className="shadow-lg border-none bg-white/90 backdrop-blur-sm">
              <CardHeader>
                <CardTitle className="text-2xl font-bold text-blue-800">
                  {bookingPhase === 1
                    ? "Event Details"
                    : bookingPhase === 2
                    ? "NPO Selection"
                    : "Booker Details"}
                </CardTitle>
                <CardDescription>
                  {bookingPhase === 1
                    ? "Select your preferred date, time, and other event details"
                    : bookingPhase === 2
                    ? "Choose an NPO to support"
                    : "Provide your contact information and team details"}
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-6">
                {bookingPhase === 1 ? (
                  // Phase 1: Event Details
                  <>
                    {/* Date Selection */}
                    <div className="space-y-2">
                      <Label
                        htmlFor="event_date"
                        className="text-base font-medium"
                      >
                        Date <span className="text-red-500">*</span>
                      </Label>
                      <Popover>
                        <PopoverTrigger asChild>
                          <Button
                            variant="outline"
                            className={cn(
                              "w-full justify-start text-left font-normal",
                              !watch("event_date") && "text-muted-foreground"
                            )}
                          >
                            <CalendarIcon className="mr-2 h-4 w-4" />
                            {watch("event_date")
                              ? formatDate(
                                  new Date(watch("event_date")),
                                  "MMMM d, yyyy"
                                )
                              : "Select a date"}
                          </Button>
                        </PopoverTrigger>
                        <PopoverContent className="w-auto p-0">
                          <Calendar
                            mode="single"
                            selected={
                              watch("event_date")
                                ? new Date(watch("event_date"))
                                : undefined
                            }
                            onSelect={(date) => setValue("event_date", date)}
                            initialFocus
                            disabled={(date) => date < twoWeeksFromNow}
                          />
                        </PopoverContent>
                      </Popover>
                    </div>

                    {/* Time and Timezone */}
                    <div className="space-y-2 flex flex-col sm:flex-row gap-4">
                      <div className="flex-1">
                        <Label
                          htmlFor="event_time"
                          className="text-base font-medium"
                        >
                          Time <span className="text-red-500">*</span>
                        </Label>
                        <div className="relative">
                          <Clock className="absolute left-3 top-3 h-4 w-4 text-gray-500" />
                          <Input
                            id="event_time"
                            type="time"
                            {...register("event_time")}
                            className="pl-10"
                          />
                        </div>
                      </div>
                      <div className="flex-1">
                        <Label
                          htmlFor="timezone"
                          className="text-base font-medium"
                        >
                          Timezone
                        </Label>
                        <Controller
                          name="timezone"
                          control={control}
                          render={({ field: { value, onChange } }) => (
                            <Select value={value} onValueChange={onChange}>
                              <SelectTrigger className="w-full">
                                <SelectValue placeholder="Select timezone" />
                              </SelectTrigger>
                              <SelectContent className="max-h-[300px]">
                                <div className="p-2">
                                  <Input
                                    placeholder="Search timezones..."
                                    className="mb-2"
                                    onChange={(e) => {
                                      // This input is just for filtering the dropdown options
                                      // The actual filtering happens in the DOM via CSS
                                      const searchTerm =
                                        e.target.value.toLowerCase();
                                      document
                                        .querySelectorAll(
                                          "[data-timezone-item]"
                                        )
                                        .forEach((item) => {
                                          const itemText =
                                            item.textContent?.toLowerCase() ||
                                            "";
                                          if (itemText.includes(searchTerm)) {
                                            item.classList.remove("hidden");
                                          } else {
                                            item.classList.add("hidden");
                                          }
                                        });
                                    }}
                                  />
                                </div>
                                <div className="max-h-[250px] overflow-auto">
                                  {timezoneOptions.map((tz) => (
                                    <SelectItem
                                      key={tz.value}
                                      value={tz.value}
                                      data-timezone-item
                                    >
                                      {tz.label}
                                    </SelectItem>
                                  ))}
                                </div>
                              </SelectContent>
                            </Select>
                          )}
                        />
                      </div>
                    </div>

                    {/* Duration Selection */}
                    <div className="space-y-2">
                      <Label
                        htmlFor="duration"
                        className="text-base font-medium"
                      >
                        Duration
                      </Label>
                      <Controller
                        name="duration"
                        control={control}
                        render={({ field: { value, onChange } }) => (
                          <Select
                            value={value.toString()}
                            onValueChange={(value) => onChange(parseInt(value))}
                          >
                            <SelectTrigger className="w-full">
                              <SelectValue placeholder="Select duration" />
                            </SelectTrigger>
                            <SelectContent>
                              {durationOptions.map((option) => (
                                <SelectItem
                                  key={option.value}
                                  value={option.value.toString()}
                                >
                                  {option.label}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        )}
                      />
                    </div>

                    {/* Volunteer Count */}
                    <div className="space-y-2">
                      <Label
                        htmlFor="volunteer_count"
                        className="text-base font-medium"
                      >
                        Number of Volunteers
                      </Label>
                      <div className="relative">
                        <Users className="absolute left-3 top-3 h-4 w-4 text-gray-500" />
                        <Input
                          id="volunteer_count"
                          type="number"
                          min="1"
                          {...register("volunteer_count", {
                            valueAsNumber: true,
                          })}
                          className="pl-10"
                          inputMode="numeric"
                          onWheel={(e) => e.currentTarget.blur()}
                        />
                      </div>
                    </div>

                    {/* Preferred Language */}
                    <div className="space-y-2">
                      <Label
                        htmlFor="preferred_language"
                        className="text-base font-medium"
                      >
                        Preferred Language
                      </Label>
                      <Controller
                        name="preferred_language"
                        control={control}
                        render={({ field: { value, onChange } }) => (
                          <Select value={value} onValueChange={onChange}>
                            <SelectTrigger className="w-full">
                              <SelectValue placeholder="Select language" />
                            </SelectTrigger>
                            <SelectContent>
                              {languageOptions.map((language) => (
                                <SelectItem key={language} value={language}>
                                  {language}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        )}
                      />
                    </div>

                    {opportunityData?.format?.toLowerCase() !== "virtual" &&
                      campaign?.campaign?.meta?.locations &&
                      campaign.campaign.meta.locations.length > 0 && (
                        <div className="space-y-2">
                          <Label
                            htmlFor="location"
                            className="text-base font-medium"
                          >
                            Event Location
                          </Label>
                          <div className="relative">
                            <Controller
                              name="location"
                              control={control}
                              render={({ field: { value, onChange } }) => (
                                <Select value={value} onValueChange={onChange}>
                                  <SelectTrigger className="w-full pl-10">
                                    <SelectValue placeholder="Select a location" />
                                  </SelectTrigger>
                                  <MapPin className="absolute left-3 top-3 h-4 w-4 text-gray-500" />
                                  <SelectContent>
                                    {campaign.campaign.meta.locations.map(
                                      (location: any) => {
                                        const locationString = `${location.district}, ${location.state}, ${location.country}`;
                                        const locationKey = `${location.country}-${location.state}-${location.district}`;
                                        return (
                                          <SelectItem
                                            key={locationKey}
                                            value={locationString}
                                          >
                                            {locationString}
                                          </SelectItem>
                                        );
                                      }
                                    )}
                                  </SelectContent>
                                </Select>
                              )}
                            />
                          </div>
                        </div>
                      )}

                    {/* Description */}
                    <div className="space-y-2">
                      <Label
                        htmlFor="description"
                        className="text-base font-medium"
                      >
                        Additional Notes
                      </Label>
                      <Textarea
                        id="description"
                        {...register("description")}
                        placeholder="Any additional details or special requirements"
                        className="min-h-[100px]"
                      />
                    </div>
                  </>
                ) : bookingPhase === 2 ? (
                  // Phase 2: NPO Selection
                  <>
                    <div className="mb-4">
                      <h3 className="text-lg font-medium text-gray-800 mb-2">
                        Select an NPO to power this event{" "}
                        <span className="text-red-500">*</span>
                      </h3>
                      <p className="text-gray-600 text-sm">
                        Choose from the following non-profit organizations that
                        support this opportunity.
                      </p>
                    </div>

                    <div className="grid grid-cols-1 gap-4 max-h-[400px] overflow-y-auto pr-2 custom-scrollbar">
                      {isLoadingNpos ? (
                        <div className="flex justify-center py-8">
                          <div className="flex flex-col items-center">
                            <Loader2 className="h-8 w-8 animate-spin text-blue-600" />
                            <p className="mt-2 text-sm text-gray-500">
                              Loading NPO partners...
                            </p>
                          </div>
                        </div>
                      ) : npos.length === 0 ? (
                        <div className="flex flex-col items-center justify-center py-8 text-center">
                          <AlertTriangle className="h-12 w-12 text-amber-500 mb-4" />
                          <h3 className="text-lg font-semibold text-gray-900">
                            No NPO Partners Available
                          </h3>
                          <p className="mt-2 text-sm text-gray-500 max-w-md">
                            There are currently no NPO partners available for
                            this opportunity. Please try a different opportunity
                            or contact support for assistance.
                          </p>
                        </div>
                      ) : (
                        npos.map((npo) => (
                          <div
                            key={npo.id}
                            className={`p-4 border rounded-lg transition-all cursor-pointer ${
                              watch("npo_id") === npo.id
                                ? "border-blue-500 bg-blue-50 shadow-md"
                                : "border-gray-200 hover:border-blue-300"
                            }`}
                            onClick={() => {
                              setValue("npo_id", npo.id);
                              setValue("catalog_id", npo.catalogId || "");
                            }}
                          >
                            <div className="flex items-start gap-4">
                              <div className="flex-shrink-0">
                                {npo.logo ? (
                                  <img
                                    src={npo.logo}
                                    alt={npo.name}
                                    className="w-16 h-16 rounded-md object-cover"
                                  />
                                ) : (
                                  <div className="w-16 h-16 rounded-md bg-blue-100 flex items-center justify-center">
                                    <Building className="w-8 h-8 text-blue-600" />
                                  </div>
                                )}
                              </div>

                              <div className="flex-1">
                                <div className="flex items-center justify-between">
                                  <h4 className="text-lg font-semibold text-gray-900">
                                    {npo.name}
                                  </h4>
                                  <div
                                    className={`w-5 h-5 rounded-full border ${
                                      watch("npo_id") === npo.id
                                        ? "border-blue-500 bg-blue-500"
                                        : "border-gray-300"
                                    }`}
                                  >
                                    {watch("npo_id") === npo.id && (
                                      <svg
                                        xmlns="http://www.w3.org/2000/svg"
                                        viewBox="0 0 24 24"
                                        fill="white"
                                        className="w-5 h-5"
                                      >
                                        <path
                                          fillRule="evenodd"
                                          d="M19.916 4.626a.75.75 0 01.208 1.04l-9 13.5a.75.75 0 01-1.154.114l-6-6a.75.75 0 011.06-1.06l5.353 5.353 8.493-12.739a.75.75 0 011.04-.208z"
                                          clipRule="evenodd"
                                        />
                                      </svg>
                                    )}
                                  </div>
                                </div>

                                <p className="text-sm text-gray-600 mt-1">
                                  {npo.description}
                                </p>

                                <div className="flex items-center text-sm text-gray-500 mt-2">
                                  <MapPin className="w-4 h-4 mr-1" />
                                  <span>{npo.location}</span>
                                </div>

                                {/* Causes section */}
                                <div className="flex flex-wrap gap-1 mt-2">
                                  {npo.causes?.map((cause, index) => (
                                    <span
                                      key={`cause-${index}`}
                                      className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800"
                                    >
                                      <Heart className="w-3 h-3 mr-1" />
                                      {cause}
                                    </span>
                                  ))}
                                </div>

                                {/* Supported Locations section */}
                                {npo.supportedLocations &&
                                  npo.supportedLocations.length > 0 && (
                                    <div className="mt-3">
                                      <h5 className="text-sm font-medium text-gray-700 mb-1">
                                        Supported Locations:
                                      </h5>
                                      <div className="flex flex-wrap gap-1">
                                        {npo.supportedLocations.map(
                                          (location, index) => (
                                            <span
                                              key={`location-${index}`}
                                              className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800"
                                            >
                                              <MapPin className="w-3 h-3 mr-1" />
                                              {location}
                                            </span>
                                          )
                                        )}
                                      </div>
                                    </div>
                                  )}
                              </div>
                            </div>
                          </div>
                        ))
                      )}
                    </div>
                  </>
                ) : (
                  // Phase 3: Booker Details
                  <>
                    {/* Name */}
                    <div className="space-y-2">
                      <Label htmlFor="name" className="text-base font-medium">
                        Full Name <span className="text-red-500">*</span>
                      </Label>
                      <div className="relative">
                        <User className="absolute left-3 top-3 h-4 w-4 text-gray-500" />
                        <Input
                          id="name"
                          type="text"
                          {...register("name")}
                          placeholder="Enter your full name"
                          className="pl-10"
                        />
                      </div>
                    </div>

                    {/* Team Name */}
                    <div className="space-y-2">
                      <Label
                        htmlFor="team_name"
                        className="text-base font-medium"
                      >
                        Team Name
                      </Label>
                      <div className="relative">
                        <Users className="absolute left-3 top-3 h-4 w-4 text-gray-500" />
                        <Input
                          id="team_name"
                          type="text"
                          {...register("team_name")}
                          placeholder="Enter your team name (optional)"
                          className="pl-10"
                        />
                      </div>
                    </div>

                    {/* Email */}
                    <div className="space-y-2">
                      <Label htmlFor="email" className="text-base font-medium">
                        Official Email Address{" "}
                        <span className="text-red-500">*</span>
                      </Label>
                      <div className="relative">
                        <Mail className="absolute left-3 top-3 h-4 w-4 text-gray-500" />
                        <Input
                          id="email"
                          type="email"
                          {...register("email")}
                          placeholder="Enter your official email address"
                          className="pl-10"
                        />
                      </div>
                    </div>

                    {/* Phone */}
                    <div className="space-y-2">
                      <Label htmlFor="phone" className="text-base font-medium">
                        Phone Number
                      </Label>
                      <div className="relative">
                        <Phone className="absolute left-3 top-3 h-4 w-4 text-gray-500" />
                        <Input
                          id="phone"
                          type="tel"
                          {...register("phone")}
                          placeholder="Enter your phone number (optional)"
                          className="pl-10"
                        />
                      </div>
                    </div>

                    {/* Address */}
                    <div className="space-y-2">
                      <Label
                        htmlFor="address"
                        className="text-base font-medium"
                      >
                        Event Venue Address{" "}
                        <span className="text-red-500">*</span>
                      </Label>
                      <div className="relative">
                        <MapPinIcon className="absolute left-3 top-3 h-4 w-4 text-gray-500" />
                        <Textarea
                          id="address"
                          {...register("address")}
                          placeholder="Enter the full address for the event"
                          className="pl-10 pt-2 min-h-[100px]"
                        />
                      </div>
                    </div>

                    {/* Terms and Conditions */}
                    <div className="space-y-2">
                      <div className="text-sm text-gray-600 italic">
                        By booking, you agree to the{" "}
                        <a href="#" className="text-blue-600 hover:underline">
                          terms and conditions
                        </a>
                      </div>
                    </div>

                    {/* Booking Summary */}
                    <div className="mt-6 p-4 bg-gray-50 rounded-lg">
                      <h4 className="font-medium text-gray-800 mb-3">
                        Booking Summary
                      </h4>
                      <div className="space-y-2 text-sm">
                        <div className="flex justify-between">
                          <span className="text-gray-600">Date:</span>
                          <span className="font-medium">
                            {watch("event_date")
                              ? formatDate(
                                  new Date(watch("event_date")),
                                  "MMMM d, yyyy"
                                )
                              : "Not selected"}
                          </span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-gray-600">Time:</span>
                          <span className="font-medium">
                            {watch("event_time") || "Not selected"}
                          </span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-gray-600">Duration:</span>
                          <span className="font-medium">
                            {durationOptions.find(
                              (option) => option.value === watch("duration")
                            )?.label || "60 minutes"}
                          </span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-gray-600">Volunteers:</span>
                          <span className="font-medium">
                            {watch("volunteer_count")}
                          </span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-gray-600">Language:</span>
                          <span className="font-medium">
                            {watch("preferred_language")}
                          </span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-gray-600">NPO:</span>
                          <span className="font-medium">
                            {npos.find((npo) => npo.id === watch("npo_id"))
                              ?.name || "Not selected"}
                          </span>
                        </div>
                        {opportunity?.data?.price_recommendations && (
                          <div className="flex justify-between pt-2 border-t border-gray-200 mt-2">
                            <span className="text-gray-800 font-medium">
                              Estimated Cost:
                            </span>
                            <span className="font-bold text-primary">
                              {new Intl.NumberFormat("en-IN", {
                                style: "currency",
                                currency:
                                  opportunity.data.price_recommendations
                                    .currency || "INR",
                                minimumFractionDigits: 0,
                                maximumFractionDigits: 0,
                              }).format(calculateEventCost())}
                            </span>
                          </div>
                        )}
                      </div>
                    </div>
                  </>
                )}
              </CardContent>
              <CardFooter>
                {bookingPhase === 1 ? (
                  <Button
                    className="w-full bg-gradient-to-r from-blue-600 to-blue-700 hover:from-blue-700 hover:to-blue-800"
                    onClick={handleProceedToNPOSelection}
                  >
                    Continue to NPO Selection
                    <ChevronRight className="ml-2 h-4 w-4" />
                  </Button>
                ) : bookingPhase === 2 ? (
                  <div className="w-full flex flex-col sm:flex-row gap-4">
                    <Button
                      variant="outline"
                      className="flex-1"
                      onClick={() => setBookingPhase(1)}
                      disabled={isSubmitting}
                    >
                      <ArrowLeft className="mr-2 h-4 w-4" />
                      Back to Event Details
                    </Button>
                    <Button
                      className="flex-1 bg-gradient-to-r from-blue-600 to-blue-700 hover:from-blue-700 hover:to-blue-800"
                      onClick={handleProceedToBookerDetails}
                      disabled={isSubmitting}
                    >
                      Continue to Booker Details
                      <ChevronRight className="ml-2 h-4 w-4" />
                    </Button>
                  </div>
                ) : (
                  <div className="w-full flex flex-col sm:flex-row gap-4">
                    <Button
                      variant="outline"
                      className="flex-1"
                      onClick={() => setBookingPhase(2)}
                      disabled={isSubmitting}
                    >
                      <ArrowLeft className="mr-2 h-4 w-4" />
                      Back to NPO Selection
                    </Button>
                    <Button
                      className="flex-1 bg-gradient-to-r from-blue-600 to-blue-700 hover:from-blue-700 hover:to-blue-800"
                      onClick={handleSubmit}
                      disabled={isSubmitting}
                    >
                      {isSubmitting ? (
                        <>
                          <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                          Processing...
                        </>
                      ) : (
                        "Complete Booking"
                      )}
                    </Button>
                  </div>
                )}
              </CardFooter>
            </Card>
          </div>
        </div>
      </div>
    </div>
  );
};

export default EventBookingPage;
